import { Alert, Box, CircularProgress } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CurrentUser } from "@rossum/upcloud-costs-shared";

import { fetchCurrentUser, login } from "../api/auth-api";
import { ApiError } from "../api/http-client";
import { CurrentUserContext } from "./current-user";

type State = { status: "loading" } | { status: "ready"; user: CurrentUser } | { status: "error"; code: string };

/** Renders its children for a user with the `cost-viewer` role; redirects to login without a session. */
export function RequireUser({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const [state, setState] = useState<State>({ status: "loading" });

    useEffect(() => {
        fetchCurrentUser().then(
            (user) => {
                setState({ status: "ready", user });
            },
            (error: unknown) => {
                if (error instanceof ApiError && error.status === 401) {
                    login();
                    return;
                }
                console.error(error);
                setState({ status: "error", code: error instanceof ApiError ? error.code : "INTERNAL_ERROR" });
            },
        );
    }, []);

    if (state.status === "loading") {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress aria-label={t("auth.redirecting")} />
            </Box>
        );
    }
    if (state.status === "error") {
        return (
            <Alert severity="error" sx={{ m: 4 }}>
                {t(`error.${state.code}`, { defaultValue: t("error.INTERNAL_ERROR") })}
            </Alert>
        );
    }
    if (!state.user.roles.includes("cost-viewer")) {
        return (
            <Alert severity="warning" sx={{ m: 4 }}>
                {t("auth.noAccess")}
            </Alert>
        );
    }
    return <CurrentUserContext.Provider value={state.user}>{children}</CurrentUserContext.Provider>;
}
